/**
 * useContextEmails Hook
 *
 * Fetches email metadata for a list of contextEmailIds using the
 * dedicated backend endpoint.
 */
import { useQuery } from '@tanstack/react-query';

import { postInboxContextEmails } from '@/api/generated/inbox/inbox';
import type { GraphMessage } from '@/api/types/inbox';
import { queryKeys } from '@/config/query/queryKeys';

interface UseContextEmailsOptions {
  contextEmailIds?: string[] | undefined;
  enabled?: boolean;
}

export const useContextEmails = ({
  contextEmailIds,
  enabled = true,
}: UseContextEmailsOptions = {}) => {
  const hasIds = !!contextEmailIds && contextEmailIds.length > 0;

  const query = useQuery({
    enabled: enabled && hasIds,
    queryFn: async (): Promise<Array<GraphMessage>> => {
      const response = await postInboxContextEmails({
        contextEmailIds: contextEmailIds ?? [],
      });
      // Map generated types to GraphMessage for consumer compatibility
      return (response ?? []).map(
        (item): GraphMessage => ({
          ...(item.bodyPreview != null && { bodyPreview: item.bodyPreview }),
          ...(item.from && { from: { address: item.from.email, name: item.from.name } }),
          id: item.id,
          ...(item.isRead != null && { isRead: item.isRead }),
          receivedDateTime: item.receivedDateTime ?? '',
          sender: item.from
            ? { emailAddress: { address: item.from.email, name: item.from.name } }
            : { emailAddress: { address: '', name: '' } },
          subject: item.subject ?? '',
        }),
      );
    },
    queryKey: queryKeys.inbox.contextEmails(contextEmailIds),
    refetchOnWindowFocus: true,
    staleTime: 5 * 60 * 1000,
  });

  return query;
};
